import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { optInCapturing, optOutCapturing } from '@/utils/analytics'

/**
 * Consent management store for PostHog tracking (FR-48).
 * Persists user consent preferences to localStorage.
 */

interface ConsentState {
  // User's explicit consent for analytics tracking
  analyticsConsent: boolean | null // null = not asked yet, true = consented, false = declined
  
  // Timestamp of last decline (ISO string)
  lastDeclineDate: string | null
  
  // Actions
  giveConsent: () => void
  withdrawConsent: () => void
  resetConsent: () => void
  checkAndResetIfExpired: () => void
}

const DECLINE_EXPIRY_DAYS = 30

const useConsentStore = create<ConsentState>()(
  persist(
    (set, get) => ({
      analyticsConsent: null,
      lastDeclineDate: null,

      /**
       * FR-48: User provides consent for analytics tracking.
       * Enables PostHog capturing, autocapture, and session recording.
       */
      giveConsent: () => {
        set({ analyticsConsent: true, lastDeclineDate: null })
        optInCapturing()
      },

      /**
       * FR-48: User withdraws consent for analytics tracking.
       * Disables all PostHog capturing and session recording.
       * Stores the decline timestamp to re-prompt after 30 days.
       */
      withdrawConsent: () => {
        set({ 
          analyticsConsent: false, 
          lastDeclineDate: new Date().toISOString() 
        })
        optOutCapturing()
      },

      /**
       * Reset consent to not asked (for testing or account deletion).
       */
      resetConsent: () => {
        set({ analyticsConsent: null, lastDeclineDate: null })
        optOutCapturing()
      },

      /**
       * Check if 30 days have passed since last decline.
       * If yes, reset consent to null to show banner again.
       */
      checkAndResetIfExpired: () => {
        const state = get()
        
        if (state.analyticsConsent === false && state.lastDeclineDate) {
          const lastDecline = new Date(state.lastDeclineDate)
          const now = new Date()
          const daysSinceDecline = Math.floor(
            (now.getTime() - lastDecline.getTime()) / (1000 * 60 * 60 * 24)
          )
          
          if (daysSinceDecline >= DECLINE_EXPIRY_DAYS) {
            set({ analyticsConsent: null, lastDeclineDate: null })
          }
        }
      },
    }),
    {
      name: 'thrive-analytics-consent', // localStorage key
      version: 2, // Increment version for new fields
    }
  )
)

export default useConsentStore
